import type { Database } from 'node-sqlite3-wasm';
import { hashJsonUtf8Sha256 } from './hash-json-sha256.js';

export type AgentCapabilityCacheRow = {
  workspaceId: string;
  agentType: string;
  configOptions: unknown[];
};

/** True when SQLite has a non-empty `configOptions` snapshot for this workspace + agent type. */
export function hasNonEmptyAgentCapabilityCache(db: Database, workspaceId: string, agentType: string): boolean {
  const t = agentType.trim();
  if (!t) return false;
  try {
    const row = db.get(
      `SELECT config_options_json FROM agent_capabilities WHERE workspace_id = ? AND agent_type = ?`,
      [workspaceId, t],
    ) as { config_options_json: string } | undefined;
    if (row?.config_options_json == null || row.config_options_json === '') return false;
    const parsed = JSON.parse(row.config_options_json) as unknown;
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

/**
 * Persist last `configOptions` per workspace + agent type. Returns whether the content hash changed.
 */
export function upsertCliAgentCapabilityCache(db: Database, row: AgentCapabilityCacheRow): boolean {
  const t = row.agentType.trim();
  if (!t) return false;
  const hash = hashJsonUtf8Sha256(row.configOptions);
  try {
    const prev = db.get(
      `SELECT content_hash FROM agent_capabilities WHERE workspace_id = ? AND agent_type = ?`,
      [row.workspaceId, t],
    ) as { content_hash: string } | undefined;
    if (prev?.content_hash === hash) return false;
  } catch {
    /* first insert */
  }
  const json = JSON.stringify(row.configOptions);
  const now = new Date().toISOString();
  db.run(
    `INSERT INTO agent_capabilities (workspace_id, agent_type, config_options_json, content_hash, updated_at)
     VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(workspace_id, agent_type) DO UPDATE SET
       config_options_json = excluded.config_options_json,
       content_hash = excluded.content_hash,
       updated_at = excluded.updated_at`,
    [row.workspaceId, t, json, hash, now],
  );
  return true;
}

export function getCliAgentCapabilityContentHash(
  db: Database,
  workspaceId: string,
  agentType: string,
): string | null {
  const t = agentType.trim();
  if (!t) return null;
  try {
    const row = db.get(
      `SELECT content_hash FROM agent_capabilities WHERE workspace_id = ? AND agent_type = ?`,
      [workspaceId, t],
    ) as { content_hash: string } | undefined;
    const h = row?.content_hash?.trim();
    return h !== '' ? h ?? null : null;
  } catch {
    return null;
  }
}

export function listCliAgentCapabilityCacheForWorkspace(
  db: Database,
  workspaceId: string,
): Array<{ agentType: string; configOptions: unknown[] }> {
  const rows = db.all(
    `SELECT agent_type, config_options_json FROM agent_capabilities WHERE workspace_id = ?`,
    [workspaceId],
  ) as Array<{ agent_type: string; config_options_json: string }>;
  const out: Array<{ agentType: string; configOptions: unknown[] }> = [];
  for (const r of rows) {
    try {
      const parsed = JSON.parse(r.config_options_json) as unknown;
      if (!Array.isArray(parsed) || parsed.length === 0) continue;
      out.push({ agentType: r.agent_type, configOptions: parsed });
    } catch {
      /* skip */
    }
  }
  return out;
}
